import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { buildDocument, h1, table } from "@/lib/documentGenerator/docTheme";
import { APD_FIELDS, PARALLEL_SECTIONS } from "@/lib/basProcess/constants";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export const generateStagePlanWordDocument = async (
  name: string,
  content: { apd?: Record<string, string>; stages?: any[] },
  projectName?: string
) => {
  const stages = content.stages || [];
  const apd = content.apd || {};
  const documentName = name || "Skedesplan / APD-plan";

  const stamp = await createStampForCurrentUser({
    docNumber: "26",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Skeden"),
    table(
      ["Skede", "Period", "Arbetsmoment", "Samtidiga entreprenörer", "Riskpunkter", "Ansvarig"],
      stages.map((s: any) => [
        s.name,
        [s.start, s.end].filter(Boolean).join(" – "),
        s.activities,
        s.contractors,
        s.risks,
        s.responsible,
      ]),
      { columnWidths: [1500, 1500, 1900, 1700, 1600, 1160] }
    ),

    h1("2. APD-förutsättningar"),
    table(
      ["Område", "Beskrivning"],
      APD_FIELDS.map((f) => [f.label, apd[f.key]]),
      { columnWidths: [3000, 6360] }
    ),
  ];
  const footer = await documentFooter({ docNumber: "26", documentType: "skedesplan" });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "26", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `32_Skedesplan_${(name || "APD").replace(/\s+/g, "_")}.docx`, {
    documentType: "skedesplan",
    docNumber: "26",
    documentName,
    snapshot: content,
    stamp,
  });
};

export const generateParallelCoordinationWordDocument = async (
  name: string,
  content: Record<string, any[]>,
  projectName?: string
) => {
  const documentName = name || "Parallell projektering och produktion";
  const stamp = await createStampForCurrentUser({ documentName, projectName });

  const children: (Paragraph | Table)[] = PARALLEL_SECTIONS.flatMap((section, i) => [
    h1(`${i + 1}. ${section.title}`),
    table(
      ["Punkt", "Beskrivning", "Datum", "Ansvarig roll", "Kommentar"],
      (content[section.key] || []).map((r: any) => [
        r.title,
        r.detail,
        r.date,
        r.owner,
        r.consequence,
      ]),
      { columnWidths: [1900, 2700, 1300, 1600, 1860] }
    ),
  ]);
  const footer = await documentFooter({ docNumber: undefined, documentType: "parallell_samordning" });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Samordning_${(name || "Parallell").replace(/\s+/g, "_")}.docx`, {
    documentType: "parallell_samordning",
    documentName,
    snapshot: content,
    stamp,
  });
};

// ---------- Samordningsmötesprotokoll ----------

export interface CoordinationMeetingExport {
  meetingDate: string;
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  participants?: string | null;
  stageName?: string | null;
  agenda: { label: string; text: string }[];
  decisions: { text: string; responsible?: string; dueDate?: string; done?: boolean; fromMeeting?: string }[];
  projectName?: string;
}


export const generateCoordinationMeetingWordDocument = async (params: CoordinationMeetingExport) => {
  const documentName = `Samordningsmöte ${params.meetingDate}`;
  const stamp = await createStampForCurrentUser({
    docNumber: "29",
    documentName,
    projectName: params.projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Mötesuppgifter"),
    table(
      ["Uppgift", "Värde"],
      [
        ["Datum", params.meetingDate],
        [
          "Tid",
          params.startTime
            ? `${String(params.startTime).slice(0, 5)}${params.endTime ? `–${String(params.endTime).slice(0, 5)}` : ""}`
            : "–",
        ],
        ["Plats", params.location || "–"],
        ["Etapp", params.stageName || "Hela projektet"],
        ["Deltagare", params.participants || "–"],

        ["Protokoll uttaget", new Date().toLocaleString("sv-SE")],
      ],
      { columnWidths: [3000, 6360] }
    ),

    h1("2. Agenda och genomgång"),
    table(
      ["Punkt", "Genomgång"],
      params.agenda.map((a) => [a.label, a.text || "–"]),
      { columnWidths: [3000, 6360] }
    ),

    h1("3. Beslut och åtgärder"),
    table(
      ["Beslut/åtgärd", "Ansvarig", "Klart senast", "Status", "Från möte"],
      params.decisions.length
        ? params.decisions.map((d) => [
            d.text,
            d.responsible || "",
            d.dueDate || "",
            d.done ? "Stängd" : "Öppen",
            d.fromMeeting || "Detta möte",
          ])
        : [["Inga beslut registrerade", "", "", "", ""]],
      { columnWidths: [3400, 1700, 1400, 1200, 1660] }
    ),
  ];

  const footer = await documentFooter({ docNumber: "29", documentType: "samordningsmote" });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "29",
      documentName,
      projectName: params.projectName,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `29_Samordningsmote_${params.meetingDate.replace(/\s+/g, "_")}.docx`,
    {
      documentType: "samordningsmote",
      docNumber: "29",
      documentName,
      snapshot: params as any,
      stamp,
    }
  );
};
